import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, ArrowRight, ChevronDown, ChevronUp, Zap, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import welcomeHeroBg from "@/assets/welcome-hero-bg.jpg";
import faceFableLogo from "@/assets/facefable-logo.png";
import SEO from "@/components/SEO";
import analytics from "@/lib/mixpanel";
import { usePerformanceTest } from "@/hooks/usePerformanceTest";

export default function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(true);

  usePerformanceTest();

  useEffect(() => {
    let mounted = true;
    
    // Check if user is already logged in
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (mounted && session) {
          navigate("/home", { replace: true });
        }
      })
      .catch((error) => {
        console.error('Session check failed:', error);
      });

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (mounted && event === 'SIGNED_IN' && session) {
        navigate("/home", { replace: true });
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) {
        // Track failed login attempt
        analytics.track('Login Failed', {
          error_message: error.message,
          method: 'email'
        });
        toast.error(error.message);
        return;
      }

      if (data.session) {
        // Check beta status before allowing login
        const { data: betaConfig } = await supabase
          .from('beta_config')
          .select('is_active')
          .maybeSingle();

        if (betaConfig?.is_active) {
          // Beta program is active - check if user is a beta participant
          const { data: participant } = await supabase
            .from('beta_participants')
            .select('is_active')
            .eq('user_id', data.user.id)
            .maybeSingle();

          if (!participant || !participant.is_active) {
            // User is not in beta program - sign them out and redirect
            await supabase.auth.signOut();
            toast.error("Beta access required. Please use a valid invite link to join.");
            navigate("/waitlist");
            return;
          }
        }

        // Track successful login
        analytics.identify(data.user.id, {
          email: data.user.email,
          last_login: new Date().toISOString()
        });
        
        analytics.track('Login Successful', {
          method: 'email',
          user_id: data.user.id
        });
        
        navigate("/home");
      }
    } catch (error) {
      analytics.track('Login Error', {
        error_type: 'unexpected'
      });
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'apple') => {
    setIsLoading(true);
    
    // Track social login attempt
    analytics.track('Social Login Attempted', {
      provider: provider
    });
    
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/home`,
        }
      });

      if (error) {
        analytics.track('Social Login Failed', {
          provider: provider,
          error_message: error.message
        });
        toast.error(error.message);
        return;
      }
    } catch (error) {
      analytics.track('Social Login Error', {
        provider: provider,
        error_type: 'unexpected'
      });
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>
      <SEO 
        title="Login"
        description="Sign in to your FaceFable account to access your personalised skincare routine, track your FaceScore progress, and connect with the community."
        noindex={true}
      />
      <div className="min-h-screen bg-background">
      {/* Content */}
      <div className="flex flex-col min-h-screen">
        {/* Navigation with logo */}
        <nav className="flex items-center justify-center p-6">
          <img src={faceFableLogo} alt="FaceFable" className="h-12" width={144} height={48} fetchPriority="high" />
        </nav>

        {/* Main content */}
        <main className="flex-1 flex flex-col justify-start items-center px-6 pt-12">
          <div className="w-full max-w-md animate-fade-in">
            <Card className="shadow-card border-border/50">
              <CardHeader className="space-y-2 text-center pt-6 pb-4">
                <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Continue your skincare journey
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-3 pt-0">
                {/* Email & Password Login Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Email Field */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="Enter your email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="pl-10 h-11 text-sm"
                          required
                        />
                      </div>
                    </div>

                    {/* Password Field */}
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="pl-10 pr-10 h-11 text-sm"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Remember Me & Forgot Password */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="rememberMe"
                          checked={formData.rememberMe}
                          onCheckedChange={(checked) => 
                            setFormData({ ...formData, rememberMe: checked as boolean })
                          }
                        />
                        <Label htmlFor="rememberMe" className="text-sm text-muted-foreground">
                          Remember me
                        </Label>
                      </div>
                      <Link 
                        to="/forgot-password" 
                        className="text-sm text-primary hover:underline"
                      >
                        Forgot password?
                      </Link>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-11 text-sm font-medium" 
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          <span>Signing in...</span>
                        </div>
                      ) : (
                        'Sign In'
                      )}
                  </Button>
                </form>

                {/* Footer */}
                <div className="text-center pt-4 border-t border-muted/20">
                  <p className="text-muted-foreground text-sm mb-2">New to FaceFable?</p>
                  <Button variant="outline" className="w-full h-11 text-sm font-medium bg-primary/10 hover:bg-primary/20 border-primary/20" asChild>
                    <Link to="/beta">
                      Create Your Account
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
    </>
  );
}